import { CivilStatus } from '@idc-forms/types/civil-status'
import { Gender } from '@idc-forms/types/gender'
import { MigrationStatus } from '@idc-forms/types/migration-status'
import { z } from 'zod'
import { IdentificationCardSchema } from '../../domains/identification'
import { LastNameSchema } from '../../domains/last-name'
import { NameSchema } from '../../domains/name'
import { PlaceSchema } from '../../domains/place'
import { ResidenceSchema } from '../../domains/residence'
import { email, nitOptional, optionalString, requiredString } from '../../primitives'
import { MESSAGES } from '../../utils/messages'
import { createProgressiveSchema, type ProgressiveLevel } from '../../utils/progressive'

/**
 * Solicitante individual. Espejo de `ApplicantIndividual` del front
 * (`features/individual/models/individual-schema.ts`) y de la forma que vive en
 * `person-individual.applicant` (Mixed en legacy — aquí le damos contrato tipado).
 *
 * NOTA condicionales (a portar): `otherProfession` requerido si profesión = OTRA;
 * `otherMigrationStatus` requerido si migrationStatus = OTHER. Ver `utils/conditional.ts`.
 */
const base = z.object({
  name: NameSchema,
  lastName: LastNameSchema,
  identificationCard: IdentificationCardSchema,
  birthplace: PlaceSchema,
  birthdate: requiredString(),
  nationality: z.array(requiredString()).min(1, MESSAGES.REQUIRED),
  migrationStatus: z.nativeEnum(MigrationStatus).optional(),
  otherMigrationStatus: optionalString(),
  gender: z.nativeEnum(Gender, { required_error: MESSAGES.REQUIRED }),
  civilStatus: z.nativeEnum(CivilStatus, { required_error: MESSAGES.REQUIRED }),
  profession: requiredString(),
  otherProfession: optionalString(),
  nit: nitOptional(),
  economicActivityRTU: optionalString(),
  phone: z.array(requiredString()).min(1, MESSAGES.REQUIRED),
  email: email(),
  residence: ResidenceSchema,
  // Declaraciones del envío (paso de revisión). `ownAct` (beneficiario final) ya se
  // valida por nivel; `truthfulness` (veracidad jurada) y `dataConsent` (tratamiento
  // de datos) se persisten como registro firmado pero su exigencia es el gate del
  // front al enviar (no dependen del nivel progresivo). Opcionales para no romper el
  // auto-save parcial.
  ownAct: z.boolean(),
  truthfulness: z.boolean().optional(),
  dataConsent: z.boolean().optional()
})

const progressive = createProgressiveSchema({
  base,
  levels: {
    // Mínimo para crear el cliente (RENAP + identificación).
    draft: ['name', 'lastName', 'identificationCard'],
    // Sección "Datos personales / Identificación" del MVP + contacto base.
    partial: [
      'name',
      'lastName',
      'identificationCard',
      'birthplace',
      'birthdate',
      'nationality',
      'gender',
      'civilStatus',
      'profession',
      'phone',
      'email',
      // La residencia es obligatoria desde el nivel partial (no sólo en complete):
      // el cliente del portal siempre debe declarar dónde reside.
      'residence'
    ],
    // Expediente completo.
    complete: [
      'name',
      'lastName',
      'identificationCard',
      'birthplace',
      'birthdate',
      'nationality',
      'migrationStatus',
      'otherMigrationStatus',
      'gender',
      'civilStatus',
      'profession',
      'otherProfession',
      'nit',
      'economicActivityRTU',
      'phone',
      'email',
      'residence',
      'ownAct'
    ]
  }
})

export interface IndividualApplicantLevelOptions {
  /**
   * ObjectId de la profesión "Otro" (isOther). Si la profesión seleccionada es
   * esta, `otherProfession` pasa a ser requerido (regla condicional).
   */
  otherProfessionId?: string
}

/**
 * Espejo de createProgressiveSchema + la regla condicional de profesión. Mantiene
 * `complete`/`requiredFieldsAt`; `forLevel` acepta el id de la profesión "Otro".
 */
export const IndividualApplicant = {
  ...progressive,
  forLevel(level: ProgressiveLevel, options?: IndividualApplicantLevelOptions) {
    const schema = progressive.forLevel(level)
    const otherId = options?.otherProfessionId
    return schema.superRefine((data, ctx) => {
      const value = data as {
        profession?: string
        otherProfession?: string
        nationality?: string[]
        migrationStatus?: string
        otherMigrationStatus?: string
        residence?: { isInOwnName?: boolean; ownershipJustification?: string }
      }

      // Residencia que NO está a nombre del cliente → hay que justificarlo.
      //
      // Es cumplimiento, no cosmética: la IVE quiere saber por qué el domicilio declarado es de
      // un tercero, y un «no» sin explicación no dice nada. Mismo patrón que `otherProfession`, y
      // va acá por el mismo motivo: la regla depende de OTRO campo del mismo objeto, así que no
      // puede vivir en la forma de `ResidenceSchema`.
      //
      // Gatea contra `=== false`, no contra un valor falsy: `undefined` es "todavía no contestó"
      // —y ahí no hay nada que justificar— mientras que `false` es una respuesta dada.
      if (
        value.residence?.isInOwnName === false &&
        !String(value.residence?.ownershipJustification ?? '').trim()
      ) {
        ctx.addIssue({
          path: ['residence', 'ownershipJustification'],
          code: z.ZodIssueCode.custom,
          message: MESSAGES.REQUIRED
        })
      }

      // Profesión "Otra" → otherProfession obligatorio.
      if (otherId && value.profession === otherId && !String(value.otherProfession ?? '').trim()) {
        ctx.addIssue({
          path: ['otherProfession'],
          code: z.ZodIssueCode.custom,
          message: MESSAGES.REQUIRED
        })
      }

      // Espejo de la regla interna (FEIC): si hay nacionalidad(es) y NINGUNA es la
      // local (Guatemala), la condición migratoria es obligatoria; y si es "Otra",
      // el detalle también. 'GT' es el isoCode local (COUNTRY_LOCAL del front).
      const nationalities = Array.isArray(value.nationality) ? value.nationality : []
      const foreignOnly = nationalities.length > 0 && !nationalities.includes('GT')
      if (foreignOnly && !value.migrationStatus) {
        ctx.addIssue({
          path: ['migrationStatus'],
          code: z.ZodIssueCode.custom,
          message: MESSAGES.REQUIRED
        })
      }
      if (
        foreignOnly &&
        value.migrationStatus === MigrationStatus.OTHER &&
        !String(value.otherMigrationStatus ?? '').trim()
      ) {
        ctx.addIssue({
          path: ['otherMigrationStatus'],
          code: z.ZodIssueCode.custom,
          message: MESSAGES.REQUIRED
        })
      }
    })
  }
}

/**
 * La forma del solicitante SIN la progresión por niveles.
 *
 * `IndividualApplicant.forLevel()` es un schema de ENTRADA: modela qué campos son
 * obligatorios en cada paso del formulario. Un contrato de LECTURA no tiene pasos
 * —devuelve lo que hay guardado— así que consume esta base directamente en vez de
 * pedirle un nivel arbitrario a un schema progresivo.
 */
export const IndividualApplicantShape = base

export type IndividualApplicant = z.infer<typeof base>
